/**
 * /public/css/form.css
 */

import React from "react";
import InElement from "../../helpers/in-element.js";

const ITEMS_CONTEXTMENU_ON_SCREEN = 10;

/**
 *
 * @param {*} props
 *     className
 *     fullWidth
 *     filterField
 *     noBorder
 *     onSelect
 *     options = [{ text: "TEXT", value: "VALUE" }]
 *     selected
 *     small
 *     disabled
 * @returns
 */
export default function InputSelect(props) {
    const [show, setShow] = React.useState(false);
    const [filter, setFilter] = React.useState(null);

    const refWrap = React.useRef(null);
    const refInput = React.useRef(null);
    const refContextMenu = React.useRef(null);

    // ! TODO
    const filterField = false; // props.filterField;

    if (!props.onSelect) {
        throw new Error(`props.onSelect is "${props.onSelect}"`);
    }

    React.useEffect(() => {
        const domContextMenu = refContextMenu.current;

        if (domContextMenu) {
            if (domContextMenu.parentElement !== document.body) {
                document.body.appendChild(domContextMenu);
                domContextMenu.addEventListener("pointerup", onSelect);
            }
        }

        return () => {
            if (domContextMenu) {
                domContextMenu.removeEventListener("pointerup", onSelect);
                domContextMenu.parentElement && domContextMenu.parentElement.removeChild(domContextMenu);
            }
        };
    }, [refContextMenu]);

    React.useEffect(() => {
        addEventListener("keydown", onWindowKeyDown);
        addEventListener("click", onWindowClick);
        addEventListener("resize", updContextMenuPosition);

        return () => {
            removeEventListener("keydown", onWindowKeyDown);
            removeEventListener("click", onWindowClick);
            removeEventListener("resize", updContextMenuPosition);
        };
    }, [refWrap]);

    React.useEffect(() => {
        const domContextMenu = refContextMenu.current;
        domContextMenu.addEventListener("wheel", onContextMenuWheel);
        return () => domContextMenu.removeEventListener("wheel", onContextMenuWheel);
    }, [refContextMenu]);

    React.useEffect(() => {
        setShow(false);
        setFilter(null);
    }, [props.selected]);

    React.useEffect(() => {
        refInput.current._show_ = show;
        updContextMenuPosition();
    }, [show]);

    function onWindowKeyDown(event) {
        if (props.disabled) {
            return;
        }

        // Don't hide when click on input (filter field)
        if (filterField && event.target === refInput.current) {
            if (event.keyCode === 13) {
                if (refWrap?.current?.clButtonPreselect) {
                    props.onSelect(refWrap.current.clButtonPreselect);
                    setFilter(null);
                    setShow(false);
                }
            }

            return;
        }

        if (event.keyCode === 27) {
            setShow(false);
        }
    }

    function onWindowClick(event) {
        if (props.disabled) {
            return;
        }

        // Don't hide when click on input (filter field)
        if (filterField && event.target === refInput.current) {
            return;
        }

        if (!InElement(refWrap.current, event.target)) {
            setShow(false);
        }
    }

    function onWrapClick(event) {
        event.preventDefault();

        if (props.disabled) {
            return;
        }

        // Don't hide when click on input (filter field)
        if (filterField && event.target === refInput.current) {
            return;
        }

        if (!InElement(refWrap.current, event.target)) {
            return;
        }

        setShow(!show);
    }

    function onContextMenuWheel(event) {
        // event.preventDefault();
    }

    function updContextMenuPosition() {
        const domWrap = refWrap.current;
        const domContextMenu = refContextMenu.current;

        let top = 0;
        let left = 0;
        let width = 0;
        let height = 0;

        // position by label
        {
            const bb = domWrap.getBoundingClientRect();
            top = bb.top + bb.height;
            left = bb.left;
            width = bb.width;
        }

        // height by item size
        {
            const bb = domContextMenu.children[0].getBoundingClientRect();
            height = bb.height * ITEMS_CONTEXTMENU_ON_SCREEN * 3;

            if (top + height > innerHeight) {
                height = Math.min(innerHeight - top - bb.height * 2, height);
            }
        }

        // set
        domContextMenu.style.setProperty("top", `${top}px`);
        domContextMenu.style.setProperty("left", `${left}px`);
        domContextMenu.style.setProperty("width", `${width}px`);
        domContextMenu.style.setProperty("max-height", `${height}px`);
    }

    function onInputChange(event) {
        setFilter(event.target.value);
        setShow(true);
    }

    function onSelect(event) {
        if (!(event instanceof Event)) {
            return;
        } else if (!(event.target instanceof HTMLButtonElement)) {
            return;
        }

        event.preventDefault();
        props.onSelect(event.target?.dataset?.value);
        setShow(false);
    }

    // "Fix"
    const options = props.options.map(option => {
        return typeof option === "object" ? option : { value: option, text: option };
    });

    // To show in "input"
    const inputValue =
        filter ??
        (() => {
            for (const option of options) {
                if (option.value === props.selected) {
                    return option.text || option.value;
                }
            }

            return "";
        })();

    // Options filtering
    const filterParts = (filter || "").toLowerCase().trim().split(" ");
    const filterString = filterParts.join(" ");
    const RE1 = new RegExp(`(^${filterParts.map(fp => fp.replace(/([^\d\wа-я\s])/gi, "\\$1")).join(" ")})`, "");
    const filtered = filter
        ? options
              .map(option => {
                  let filterPoints = 0;

                  const text = option.text
                      .toLowerCase()
                      .replace(/[^\d\wа-я\s]+/g, "")
                      .replace(/(\s+)/g, " ");

                  const re1test = RE1.test(text);

                  // Best
                  filterPoints += re1test * (filterParts.length + 1) * 20;

                  // Normal
                  filterPoints += (text.indexOf(filterString) !== -1) * (filterParts.length + 1) * 10;

                  // Lower
                  let partPoints = 0;
                  for (const filterPart of filterParts) {
                      partPoints += text.indexOf(filterPart) !== -1;
                  }
                  partPoints = partPoints >= filterParts.length ? partPoints : 0;

                  filterPoints += partPoints;

                  return { option, filterPoints };
              })
              .filter(({ filterPoints }) => filterPoints > 0)
              .sort(({ filterPoints: a }, { filterPoints: b }) => (a === b ? 0 : a < b ? 1 : -1))
              .map(({ option }) => option)
        : options;

    // Class Button
    if (refWrap?.current) {
        refWrap.current.clButtonPreselect = filter ? filtered?.[0]?.value : null;
    }
    function CLButton(option) {
        const cl = [];
        props.selected == option.value && cl.push("selected");

        if (refWrap?.current) {
            refWrap.current.clButtonPreselect == option.value && cl.push("pre-selected");
        }

        return cl.join(" ");
    }

    const CLInputSelectWrap = ["input-select-wrap"];
    props.className && CLInputSelectWrap.push(props.className);
    props.small && CLInputSelectWrap.push("small");
    props.noBorder && CLInputSelectWrap.push("no-border");
    props.noPadding && CLInputSelectWrap.push("no-padding");
    props.disabled && CLInputSelectWrap.push("disabled");
    show && CLInputSelectWrap.push("show");

    const CLInputSelect = ["input-select"];
    props.fullWidth && CLInputSelect.push("full-width");

    const CLInputSelectContextmenu = ["input-select-contextmenu"];
    show && CLInputSelectContextmenu.push("show");

    const prevActiveElement = document.activeElement;
    return (
        <>
            <label
                ref={refWrap}
                className={CLInputSelectWrap.join(" ")}
                onClick={onWrapClick}
                disabled={props.disabled}
            >
                {filterField && (
                    <input
                        ref={refInput}
                        type="text"
                        className={CLInputSelect.join(" ")}
                        value={inputValue}
                        placeholder={props?.placeholder || ""}
                        onClick={() => {
                            if (prevActiveElement !== refInput.current) {
                                refInput.current.select();
                            }

                            setShow(true);
                        }}
                        onBlur={() => setTimeout(() => setFilter(null), 100)}
                        onChange={onInputChange}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        disabled={props.disabled}
                    />
                )}

                {!filterField && (
                    <span ref={refInput} className={CLInputSelect.join(" ")}>
                        {!inputValue && <span className="placeholder">{props?.placeholder || ""}</span>}
                        {inputValue && inputValue}
                    </span>
                )}

                <div className="input-select-icon" />

                <div ref={refContextMenu} className={CLInputSelectContextmenu.join(" ")}>
                    {filtered.length === 0 && (
                        <button type="button" disabled>
                            No options
                        </button>
                    )}

                    {filtered.map(option => (
                        <button key={option.value} type="button" className={CLButton(option)} data-value={option.value}>
                            {option.text || option.value}
                        </button>
                    ))}
                </div>
            </label>

            {/* TODO! Standartize .input-base-error */}
            {props.error !== undefined && <div className="input-base-error">{props.error || <>&nbsp;</>}</div>}
        </>
    );
}
